// "Audit ▾" header dropdown — a read-only inspection panel modelled on CPQ 1's
// audit menu, trimmed to the tools that have real cpq2 data behind them:
//   Data             — the live answer/table/selection state
//   Actions          — every operation and whether its rule matches right now
//   Additional Items — the extra SO lines this config adds (gasket / nipple /
//                      burner / nozzle), resolved to NetSuite items
//   Data Mapping     — the encoded custitem_mapping and its decode
//   Item Creation    — the item (name + fields) that Save & Submit would create
//   Materials        — the BOM components
//   Routing Steps    — the manufacturing routing operations
// (CPQ 1's Pricing / Images are dropped — cpq2 has no data for them: the line is
// a flat amount, and the composite image already renders in the side panel.)

import { useEffect, useMemo, useState } from "react";
import type { CpqData } from "../data/loader";
import { loadRoutings } from "../data/loader";
import { useAnswers } from "../state/AnswersContext";
import { buildAssembly, type AssemblyPreview } from "../util/assemblyBuilder";
import { extractMappingSpec, decodeMapping } from "../util/mappingDecoder";
import { evalRule } from "../util/rule";
import {
  buildAdditionalLines,
  resolveAdditionalLines,
  type AdditionalLinePayload,
} from "../util/additionalLines";
import type { AnswerState, SelectedRowState, TableState } from "../types";
import { getProfile } from "../products";
import AdminOverrideField from "./AdminOverrideField";
import ScoreboardPanel from "./ScoreboardMenu";

type Props = { data: CpqData };

// `soon: true` tools have no cpq2 data yet (the features aren't built), but are
// shown so the menu reflects the full intended set; they open a "coming soon"
// panel. Swap the placeholder for a real view when the feature ships.
const TOOLS = [
  { key: "data", label: "Data", soon: false },
  { key: "actions", label: "Actions", soon: false },
  { key: "additionalItems", label: "Additional Items", soon: false },
  { key: "mapping", label: "Data Mapping", soon: false },
  { key: "item", label: "Item Creation", soon: false },
  { key: "materials", label: "Materials", soon: false },
  { key: "pricing", label: "Pricing", soon: true },
  { key: "routing", label: "Routing Steps", soon: false },
  // The points leaderboard, a header button of its own until 2026-10-19 (Kevin: "move the
  // scoreboard into the audit").
  { key: "scoreboard", label: "Scoreboard", soon: false },
] as const;
type ToolKey = (typeof TOOLS)[number]["key"];
const NEEDS_PREVIEW = new Set<ToolKey>(["item", "materials", "routing"]);

export default function AuditMenu({ data }: Props) {
  const { answers, tables, selectedRows } = useAnswers();
  const [open, setOpen] = useState(false);
  const [tool, setTool] = useState<ToolKey | null>(null);

  // routings.json is ~7.6 MB, so only fetch it when an audit view needs it.
  const [routings, setRoutings] = useState<Awaited<ReturnType<typeof loadRoutings>> | null>(null);
  useEffect(() => {
    if (tool && NEEDS_PREVIEW.has(tool) && routings === null) {
      let alive = true;
      loadRoutings()
        .then((r) => alive && setRoutings(r))
        .catch(() => alive && setRoutings([]));
      return () => {
        alive = false;
      };
    }
  }, [tool, routings]);

  const preview = useMemo<AssemblyPreview | null>(() => {
    if (!tool || !NEEDS_PREVIEW.has(tool) || !routings) return null;
    try {
      return buildAssembly(answers, {
        itcrs: data.itcrs,
        materials: data.materials,
        routings,
        fingerprintKey: getProfile(data.slug).fingerprintAnswerKey,
        computeRouting: getProfile(data.slug).computeRouting,
        bomFallbackComponents: getProfile(data.slug).bomFallbackComponents,
        expectedComponentFamilies: getProfile(data.slug).expectedComponentFamilies,
        buildsRouting: getProfile(data.slug).buildsRouting,
      });
    } catch {
      return null;
    }
  }, [tool, routings, answers, data.itcrs, data.materials, data.slug]);

  const activeLabel = TOOLS.find((t) => t.key === tool)?.label ?? "";

  return (
    <div className="cpq-audit-wrap">
      <button
        type="button"
        className="cpq-audit-btn"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
      >
        Audit <span aria-hidden>▾</span>
      </button>

      {open && (
        <>
          <div className="cpq-audit-backdrop" onClick={() => setOpen(false)} />
          <ul className="cpq-audit-menu" role="menu">
            {TOOLS.map((t) => (
              <li key={t.key} role="none">
                <button
                  type="button"
                  role="menuitem"
                  className="cpq-audit-menu-item"
                  onClick={() => {
                    setTool(t.key);
                    setOpen(false);
                  }}
                >
                  {t.label}
                  {t.soon && <span className="cpq-audit-badge">soon</span>}
                </button>
              </li>
            ))}
          </ul>
        </>
      )}

      {tool && (
        <div
          className="cpq-audit-overlay"
          role="dialog"
          aria-modal="true"
          onClick={(e) => {
            if (e.target === e.currentTarget) setTool(null);
          }}
        >
          <div className="cpq-audit-modal">
            <header className="cpq-audit-modal-head">
              <h2>Audit — {activeLabel}</h2>
              <button
                type="button"
                className="cpq-audit-close"
                aria-label="Close"
                onClick={() => setTool(null)}
              >
                ✕
              </button>
            </header>
            <div className="cpq-audit-modal-body">
              {tool === "data" && (
                <DataView answers={answers} tables={tables} selectedRows={selectedRows} />
              )}
              {tool === "actions" && <ActionsView data={data} answers={answers} />}
              {tool === "additionalItems" && <AdditionalItemsView data={data} />}
              {tool === "mapping" && <MappingView data={data} answers={answers} />}
              {tool === "pricing" && <ComingSoon feature="Pricing" />}
              {NEEDS_PREVIEW.has(tool) && !routings && <p className="cpq-audit-note">Loading routings…</p>}
              {tool === "item" && routings && <ItemView preview={preview} />}
              {tool === "materials" && routings && <MaterialsView preview={preview} />}
              {tool === "routing" && routings && <RoutingView preview={preview} />}
              {tool === "scoreboard" && <ScoreboardPanel />}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function stringify(v: unknown): string {
  if (v === null) return "null";
  if (v === undefined) return "undefined";
  if (typeof v === "string") return `"${v}"`;
  return String(v);
}

// Placeholder for audit tools whose underlying feature isn't built yet (Pricing,
// Additional Items). Replace the body with a real view when the feature ships.
function ComingSoon({ feature }: { feature: string }) {
  return (
    <div className="cpq-audit-card">
      <header>{feature}</header>
      <p className="cpq-audit-note">
        {feature} isn’t implemented in cpq2 yet — this panel will populate once the{" "}
        {feature.toLowerCase()} feature ships.
      </p>
    </div>
  );
}

// ---- Data ----
function DataView({
  answers,
  tables,
  selectedRows,
}: {
  answers: AnswerState;
  tables: TableState;
  selectedRows: SelectedRowState;
}) {
  const [filter, setFilter] = useState("");
  const keys = Object.keys(answers).sort();
  const f = filter.trim().toLowerCase();
  const shown = f ? keys.filter((k) => k.toLowerCase().includes(f)) : keys;
  return (
    <>
      <div className="cpq-audit-card">
        <header>
          Answers — {shown.length}/{keys.length} keys
        </header>
        <input
          className="cpq-input"
          placeholder="filter keys…"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
        />
        <div className="cpq-audit-scroll">
          <table className="cpq-table cpq-audit-table">
            <thead>
              <tr>
                <th>Key</th>
                <th>Value</th>
              </tr>
            </thead>
            <tbody>
              {shown.map((k) => (
                <tr key={k}>
                  <td>
                    <code>{k}</code>
                  </td>
                  <td>
                    <code>{stringify(answers[k])}</code>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="cpq-audit-card">
        <header>Tables — {Object.keys(tables).length}</header>
        {Object.entries(tables).map(([code, rows]) => (
          <details key={code}>
            <summary>
              <code>{code}</code> — {rows.length} row{rows.length === 1 ? "" : "s"}
            </summary>
            <pre>{JSON.stringify(rows, null, 2)}</pre>
          </details>
        ))}
      </div>

      <div className="cpq-audit-card">
        <header>Selected rows</header>
        <pre>{JSON.stringify(selectedRows, null, 2)}</pre>
      </div>
    </>
  );
}

// ---- Actions ----
function ActionsView({ data, answers }: { data: CpqData; answers: AnswerState }) {
  const ops = useMemo(
    () =>
      [...data.operations].sort(
        (a, b) =>
          Number(a.custrecord_cpqc_operation_sequence || 0) -
          Number(b.custrecord_cpqc_operation_sequence || 0)
      ),
    [data.operations]
  );
  const rows = ops.map((o) => {
    const rule = (o.custrecord_cpqc_operation_parsed_rule as string) || "";
    let matches = false;
    try {
      matches = rule === "" ? true : evalRule(rule, answers);
    } catch {
      matches = false;
    }
    const runOnce = /SUBMITTED\/SUBMITTED/.test(rule);
    return { o, rule, matches, runOnce };
  });
  const matching = rows.filter((r) => r.matches).length;
  return (
    <>
      {/* The admin override lives here rather than in the header -- Kevin, 2026-08-27:
          "you should have put the function in the actions tab." */}
      <AdminOverrideField />
      <div className="cpq-audit-card">
      <header>
        Operations — {matching}/{rows.length} rules match the current state
      </header>
      <p className="cpq-audit-note">
        “Would run” means the operation’s rule evaluates true against the current
        answers (button-gated ops like Calculate only match right after a click).
      </p>
      <div className="cpq-audit-scroll">
        <table className="cpq-table cpq-audit-table">
          <thead>
            <tr>
              <th>Seq</th>
              <th>Operation</th>
              <th>Would run</th>
              <th>Rule</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(({ o, rule, matches, runOnce }) => (
              <tr key={o.id} className={matches ? "" : "cpq-audit-dim"}>
                <td>{o.custrecord_cpqc_operation_sequence}</td>
                <td>
                  <code>{o.custrecord_cpqc_operation_code || o.name}</code>
                  {runOnce && <span className="cpq-audit-badge">run-once</span>}
                </td>
                <td>{matches ? "✓" : "—"}</td>
                <td>
                  <code className="cpq-audit-rule">{rule || "(always)"}</code>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      </div>
    </>
  );
}

// ---- Additional Items ----
// The extra sales-order line items this configuration drags onto the order
// (Flange Gasket, Ext Nipple, Main Burner / Mixer, Main Nozzle — see
// src/util/additionalLines.ts). These are NOT in the BOM; each is added as its
// own line (qty 1, NetSuite's native price + tax) by the sales-order write-back.
// This view lists what the CURRENT answers resolve to, and whether each workbook
// part number resolves to a real NetSuite item (an unresolved one is skipped on
// Save, so seeing "not found" here explains a missing line).
const ADDITIONAL_KIND_LABEL: Record<string, string> = {
  gasket: "Flange Gasket",
  nipple: "Ext Nipple",
  burner: "Main Burner / Mixer",
  nozzle: "Main Nozzle",
};

function AdditionalItemsView({ data }: { data: CpqData }) {
  const { answers } = useAnswers();
  const specs = useMemo(
    () => buildAdditionalLines(answers, data.questions),
    [answers, data.questions]
  );
  // Stable signature so we only re-resolve when the actual line set changes, not
  // on every answer keystroke (answers is a fresh object each edit).
  const specKey = specs.map((s) => `${s.kind}:${s.partNumber}:${s.quantity}`).join("|");

  const [resolved, setResolved] = useState<AdditionalLinePayload[] | null>(null);
  useEffect(() => {
    let alive = true;
    if (specs.length === 0) {
      setResolved([]);
      return () => {
        alive = false;
      };
    }
    setResolved(null); // show "resolving…" until the lookup returns
    resolveAdditionalLines(specs)
      .then((r) => alive && setResolved(r))
      .catch(() => alive && setResolved([]));
    return () => {
      alive = false;
    };
    // specKey captures the spec content; specs is used inside (same source).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [specKey]);

  // Resolved payloads carry itemName = the workbook part number, so match on it.
  const byName: Record<string, AdditionalLinePayload> = {};
  for (const p of resolved ?? []) byName[p.itemName] = p;

  return (
    <div className="cpq-audit-card">
      <header>Additional line items — {specs.length}</header>
      <p className="cpq-audit-note">
        Extra sales-order lines this configuration adds on Save &amp; Submit (each
        qty 1, NetSuite’s native price + tax — none are in the BOM). Part numbers
        come from the NDB workbook; “not found” means the number didn’t resolve to
        a NetSuite item, so that line would be skipped.
      </p>
      {specs.length === 0 ? (
        <p className="cpq-audit-note">
          This configuration adds no additional line items.
        </p>
      ) : (
        <div className="cpq-audit-scroll">
          <table className="cpq-table cpq-audit-table">
            <thead>
              <tr>
                <th>Kind</th>
                <th>Part number</th>
                <th>Qty</th>
                <th>Resolved item id</th>
                <th>Line description</th>
              </tr>
            </thead>
            <tbody>
              {specs.map((s, i) => {
                const hit = byName[s.partNumber];
                const missing = resolved != null && !hit;
                return (
                  <tr key={`${s.partNumber}-${i}`} className={missing ? "cpq-audit-dim" : ""}>
                    <td>{ADDITIONAL_KIND_LABEL[s.kind] ?? s.kind}</td>
                    <td>
                      <code>{s.partNumber}</code>
                    </td>
                    <td>{s.quantity}</td>
                    <td>
                      {resolved == null ? (
                        <em>resolving…</em>
                      ) : hit ? (
                        <code>{hit.itemId}</code>
                      ) : (
                        <em>not found</em>
                      )}
                    </td>
                    <td>
                      {hit?.description ? (
                        <pre className="cpq-audit-wrappre">{hit.description}</pre>
                      ) : (
                        <span className="cpq-audit-note">native item description</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ---- Data Mapping ----
function MappingView({ data, answers }: { data: CpqData; answers: AnswerState }) {
  const encoded = String(
    answers["MAPPING/MAPPING_ANSWER"] ?? answers["MAPPING/MAPPING"] ?? ""
  );
  const decoded = useMemo(() => {
    if (!encoded) return null;
    try {
      const spec = extractMappingSpec(data.operations);
      if (!spec) return null;
      return decodeMapping(encoded, spec, data.questions);
    } catch {
      return null;
    }
  }, [encoded, data.operations, data.questions]);

  if (!encoded) {
    return (
      <p className="cpq-audit-note">
        No encoded mapping yet — it is produced reactively once the configuration
        has values. Fill the sizing requirements and select a housing first.
      </p>
    );
  }
  return (
    <>
      <div className="cpq-audit-card">
        <header>Encoded mapping (custitem_mapping)</header>
        <pre className="cpq-audit-wrappre">{encoded}</pre>
      </div>
      <div className="cpq-audit-card">
        <header>Decoded selections{decoded ? ` — ${decoded.selects.length}` : ""}</header>
        {!decoded ? (
          <p className="cpq-audit-note">Could not decode with the current mapping spec.</p>
        ) : (
          <div className="cpq-audit-scroll">
            <table className="cpq-table cpq-audit-table">
              <thead>
                <tr>
                  <th>Question</th>
                  <th>Answer code</th>
                </tr>
              </thead>
              <tbody>
                {/* Index key: decodeMapping can legitimately emit the same
                    question from more than one slot (e.g. CELL_OD occupies both
                    its dropdown slot and the derived DIMENSIONS_SIZE slot), so a
                    question- or question+code-based key would collide. */}
                {decoded.selects.map((s, i) => (
                  <tr key={`${s.question}-${i}`}>
                    <td>
                      <code>{s.question}</code>
                    </td>
                    <td>
                      <code>{s.answerCode}</code>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      {decoded && Object.keys(decoded.values).length > 0 && (
        <div className="cpq-audit-card">
          <header>Decoded values — {Object.keys(decoded.values).length}</header>
          <pre>{JSON.stringify(decoded.values, null, 2)}</pre>
        </div>
      )}
    </>
  );
}

// ---- shared warnings ----
function Warnings({ preview }: { preview: AssemblyPreview | null }) {
  if (!preview || preview.warnings.length === 0) return null;
  return (
    <div className="cpq-audit-card cpq-audit-warn">
      <header>Warnings — {preview.warnings.length}</header>
      <ul>
        {preview.warnings.map((w, i) => (
          <li key={i}>{w}</li>
        ))}
      </ul>
    </div>
  );
}

// ---- Item Creation ----
function ItemView({ preview }: { preview: AssemblyPreview | null }) {
  if (!preview) return <p className="cpq-audit-note">Could not build a preview.</p>;
  const item = preview.item;
  return (
    <>
      <Warnings preview={preview} />
      {!item ? (
        <p className="cpq-audit-note">No item would be created (see warnings).</p>
      ) : (
        <>
          <div className="cpq-audit-card">
            <header>Item</header>
            <dl className="cpq-audit-kv">
              <dt>Name (itemid)</dt>
              <dd>
                <code>{item.itemid}</code>
              </dd>
              <dt>BOM name</dt>
              <dd>{item.bomName || "—"}</dd>
              <dt>Routing name</dt>
              <dd>{item.routeName || "—"}</dd>
              <dt>Revision</dt>
              <dd>{item.revision || "—"}</dd>
              <dt>Matched ITCR</dt>
              <dd>{preview.matchedItcr || "—"}</dd>
            </dl>
          </div>
          <div className="cpq-audit-card">
            <header>Item fields — {Object.keys(item.fields).length}</header>
            <div className="cpq-audit-scroll">
              <table className="cpq-table cpq-audit-table">
                <thead>
                  <tr>
                    <th>Field</th>
                    <th>Value</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(item.fields).map(([k, v]) => (
                    <tr key={k}>
                      <td>
                        <code>{k}</code>
                      </td>
                      <td>{v === "" ? <em>(empty)</em> : String(v)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </>
  );
}

// ---- Materials (BOM) ----
function MaterialsView({ preview }: { preview: AssemblyPreview | null }) {
  if (!preview) return <p className="cpq-audit-note">Could not build a preview.</p>;
  const bom = preview.bom;
  return (
    <>
      <Warnings preview={preview} />
      <div className="cpq-audit-card">
        <header>
          BOM {bom.name ? `“${bom.name}”` : ""} — {bom.components.length} component
          {bom.components.length === 1 ? "" : "s"}
        </header>
        {bom.components.length === 0 ? (
          <p className="cpq-audit-note">No components matched the materials ruleset.</p>
        ) : (
          <div className="cpq-audit-scroll">
            <table className="cpq-table cpq-audit-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Item</th>
                  <th>Qty</th>
                </tr>
              </thead>
              <tbody>
                {bom.components.map((c, i) => (
                  <tr key={i}>
                    <td>{i + 1}</td>
                    <td>
                      <code>{c.item}</code>
                    </td>
                    <td>{c.qty}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
}

// ---- Routing Steps ----
function RoutingView({ preview }: { preview: AssemblyPreview | null }) {
  if (!preview) return <p className="cpq-audit-note">Could not build a preview.</p>;
  const r = preview.routing;
  return (
    <>
      <Warnings preview={preview} />
      <div className="cpq-audit-card">
        <header>
          Routing {r.name ? `“${r.name}”` : ""} — {r.operations.length} step
          {r.operations.length === 1 ? "" : "s"}
        </header>
        {r.operations.length === 0 ? (
          <p className="cpq-audit-note">No routing operations matched ROUTING_RULE.</p>
        ) : (
          <div className="cpq-audit-scroll">
            <table className="cpq-table cpq-audit-table">
              <thead>
                <tr>
                  <th>Seq</th>
                  <th>Operation</th>
                  <th>Work center</th>
                  <th>Setup</th>
                  <th>Run rate</th>
                </tr>
              </thead>
              <tbody>
                {r.operations.map((op, i) => (
                  <tr key={i}>
                    <td>{op.opseq}</td>
                    <td>{op.name || <code>{op.costTemplate}</code>}</td>
                    <td>{op.workCenter || op.workCenterId || "—"}</td>
                    <td>{op.setupTime || "—"}</td>
                    <td>{op.runRate || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
}
